import { addDays, todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { uid } from '../../../../lib/uid';
import {
  MAINTENANCE_ACTIVITY_TYPE,
  MAINTENANCE_RECURRENCE,
  type MaintenanceActivityType,
  type MaintenanceCategoryRequest,
  type MaintenanceItemRequest,
  type MaintenanceRecurrence,
  type RecordMaintenanceRequest,
} from '../buildingManagementTypes';

/**
 * Synthetic Building Management records, for filling a form in one click.
 *
 * **Nothing here lands in `rsys_pii`** — people are chosen by id from the live personnel feed,
 * never typed — so plausible vocabulary is fine. Anything unique is SUFFIXED.
 */

/** A category. The name is unique among live rows, so the second click must not collide. */
export function sampleMaintenanceCategory(): MaintenanceCategoryRequest {
  const suffix = uid().slice(0, 6).toUpperCase();
  const kind = pick(CATEGORY_KINDS);

  return {
    name: `${kind.name} ${suffix}`,
    description: kind.description,
    nearExpirationDays: kind.nearExpirationDays,
  };
}

/**
 * An item, resolved against the LIVE catalogs the form already loaded.
 *
 * A parameterless generator would silently fill none of the foreign keys; this one takes them
 * as arguments and leaves a field empty only where its catalog is.
 */
export function sampleMaintenanceItem(catalogs: {
  categoryIds: string[];
  buildingIds: string[];
  personnelIds: string[];
  supplierIds: string[];
}): MaintenanceItemRequest {
  const obligation = pick(OBLIGATIONS);

  return {
    categoryId: pickOrNull(catalogs.categoryIds),
    realPropertyId: pickOrNull(catalogs.buildingIds),
    location: pick(LOCATIONS),
    recurrence: obligation.recurrence,

    // Spread either side of today, so a run of clicks fills every aging band.
    dueDate: addDays(todayCalendarDate(), Math.floor(Math.random() * 160) - 60),
    responsiblePersonnelId: pickOrNull(catalogs.personnelIds),
    supplierId: pickOrNull(catalogs.supplierIds),
    remarks: pick(REMARKS),
  };
}

/** A record. The next due date is the one the server suggested, which the form already holds. */
/**
 * An activity. The next due date is filled even though the "renews the schedule" box it sits
 * behind stays at its default (unticked) — a flag that reveals a block keeps its default, and
 * what is behind it is filled for whoever ticks it.
 */
export function sampleMaintenanceRecord(suggestedNextDueDate: string, personnelIds: string[]): RecordMaintenanceRequest {
  const activity = pick(ACTIVITIES);

  return {
    type: activity.type,
    activity: activity.text,
    performedOn: todayCalendarDate(),
    performedByPersonnelId: pickOrNull(personnelIds),
    nextDueDate: suggestedNextDueDate > todayCalendarDate() ? suggestedNextDueDate : addDays(todayCalendarDate(), 30),
    remarks: pick(RECORD_REMARKS),
  };
}

function pick<T>(pool: readonly T[]): T {
  return pool[Math.floor(Math.random() * pool.length)];
}

function pickOrNull(pool: readonly string[]): string | null {
  return pool.length === 0 ? null : pick(pool);
}

const CATEGORY_KINDS = [
  { name: 'Permits', description: 'Business, building and occupancy permits that lapse on a date.', nearExpirationDays: 60 },
  { name: 'Air-conditioning', description: 'Preventive maintenance of split and central units.', nearExpirationDays: 14 },
  { name: 'Elevators', description: 'Monthly servicing and the annual safety inspection.', nearExpirationDays: 21 },
  { name: 'Fire Safety', description: 'Extinguishers, alarms and the fire safety inspection certificate.', nearExpirationDays: null },
  { name: 'Generator', description: 'Load tests and oil changes of the standby generator.', nearExpirationDays: null },
] as const;

const OBLIGATIONS: readonly { name: string; recurrence: MaintenanceRecurrence }[] = [
  { name: 'Business permit renewal', recurrence: MAINTENANCE_RECURRENCE.annual },
  { name: 'Elevator monthly servicing', recurrence: MAINTENANCE_RECURRENCE.monthly },
  { name: 'Aircon preventive maintenance', recurrence: MAINTENANCE_RECURRENCE.quarterly },
  { name: 'Fire extinguisher recharge', recurrence: MAINTENANCE_RECURRENCE.annual },
  { name: 'Generator load test', recurrence: MAINTENANCE_RECURRENCE.weekly },
  { name: 'Water tank cleaning', recurrence: MAINTENANCE_RECURRENCE.semiAnnual },
];

const LOCATIONS = ['Roof deck', 'Basement plant room', 'Ground floor lobby', 'Admin office', 'Server room'] as const;

const REMARKS = [
  'Coordinate with the building administrator a week before the visit.',
  'The contractor needs a gate pass; request it from security.',
  null,
] as const;

const ACTIVITIES: readonly { type: MaintenanceActivityType; text: string }[] = [
  { type: MAINTENANCE_ACTIVITY_TYPE.maintenance, text: 'Serviced the split-type units' },
  { type: MAINTENANCE_ACTIVITY_TYPE.maintenance, text: 'Replaced the elevator door sensor' },
  { type: MAINTENANCE_ACTIVITY_TYPE.compliance, text: 'Renewed the business permit' },
  { type: MAINTENANCE_ACTIVITY_TYPE.compliance, text: 'Passed the fire safety inspection' },
  { type: MAINTENANCE_ACTIVITY_TYPE.submission, text: 'Submitted the quarterly generator log' },
  { type: MAINTENANCE_ACTIVITY_TYPE.submission, text: 'Filed the annual building inspection report' },
];

const RECORD_REMARKS = [
  'Completed with no findings.',
  'Serviced; one filter replaced.',
  'Renewed; the new certificate is filed with this record.',
] as const;
